import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { DayLight, NtpInterval, NtpSyncEnable, TimeType } from '@EcamModel/model/SP111SysDateTime';
import { Divider, Stack, Typography } from '@mui/material';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { formatISODate, formatUKTime, getTimezoneDescription } from 'src/helpers';
import { DataSectionHasSubItem, SectionHasSubItemHeader, SectionNotSubItem } from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import useMileSightSP111, { SectionsCollapseSP111 } from '@pages/cameras/hook/useMilesightSP111';

const dayLightMapping: { [key in DayLight]: string } = {
    [DayLight.Disabled]: 'Disabled',
    [DayLight.Automatic]: 'Automatic',
};

const timeTypeMapping: { [key in TimeType]: string } = {
    [TimeType.NTP]: 'NTP server',
    [TimeType.Computer]: 'Synchronize with computer time',
    [TimeType.Manual]: 'Manual',
};

const ntpSyncEnableMapping: { [key in NtpSyncEnable]: string } = {
    [NtpSyncEnable.Disable]: 'Disable',
    [NtpSyncEnable.Enable]: 'Enable',
};

const ntpIntervalMapping: { [key in NtpInterval]: string } = {
    [NtpInterval.OneHour]: '60',
    [NtpInterval.TwoHours]: '120',
    [NtpInterval.FiveHours]: '300',
    [NtpInterval.TenHours]: '600',
    [NtpInterval.OneDay]: '1440',
    [NtpInterval.TwoDays]: '2880',
    [NtpInterval.FiveDays]: '7200',
    [NtpInterval.TenDays]: '14400',
    [NtpInterval.ThirtyDays]: '43200',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapseSP111]: boolean };
    handleToggleCollapse: (section: SectionsCollapseSP111) => void;
};

export default function DateAndTimeMilesightsCameraSP111(props: Props) {
    const { camera, filteredCamera } = useCameraDetailContext();
    const { isOpenCollapse, handleToggleCollapse } = props;
    const { SP111SysDateTime, isLoadingMilesightsSP111SysDateTime } = useMileSightSP111({
        filteredCamera,
        cameraSP111: camera?.SP111CameraId!,
    });
    const [time, setTime] = useState(formatUKTime(new Date()));

    useEffect(() => {
        const intervalId = setInterval(() => {
            setTime(formatUKTime(new Date()));
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    const statusList_CurrentSystemTime: DataSectionHasSubItem[] = [
        {
            label: 'Date:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={formatISODate(new Date())}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Time:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={time}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const statusList_SetTheSystemTime: DataSectionHasSubItem[] = [
        {
            label: 'Time zone:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={
                        <Typography color={color.secondary}>
                            {getTimezoneDescription(SP111SysDateTime?.TimeZoneTz!)}
                        </Typography>
                    }
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Daylight saving time:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={dayLightMapping[SP111SysDateTime?.DayLight!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Synchronize mode:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={timeTypeMapping[SP111SysDateTime?.TimeType!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Server address:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={SP111SysDateTime?.NtpServer!}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'NTP sync:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={ntpSyncEnableMapping[SP111SysDateTime?.NtpSyncEnable!]}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
        {
            label: 'Interval:',
            value: (
                <LoadingTextSkeleton
                    conditions={isLoadingMilesightsSP111SysDateTime}
                    value={`${ntpIntervalMapping[SP111SysDateTime?.NtpInterval!]} min (1~43200)`}
                    loadingStyle={{
                        width: '50%',
                    }}
                />
            ),
        },
    ];

    const handleEdit = () => {
        console.log('Edit button clicked');
    };
    return (
        <Stack id="currentSystemTime" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Date & Time" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                    mb: 2,
                }}
            />

            <SectionNotSubItem
                header="Set the System Time"
                statusList={statusList_SetTheSystemTime}
                isOpen={isOpenCollapse.setTheSystemTime}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.SetTheSystemTime);
                }}
            />

            <SectionNotSubItem
                name="setTheSystemTime"
                header="Current system time"
                statusList={statusList_CurrentSystemTime}
                isOpen={isOpenCollapse.currentSystemTime}
                onToggleCollapse={() => {
                    handleToggleCollapse(SectionsCollapseSP111.CurrentSystemTime);
                }}
            />
        </Stack>
    );
}
